/**
 * Places List PDF Layout
 *
 * Creates a simple table layout for places and locations per specification.
 * Shows place names, addresses, and types. Contact information and notes.
 * Basic place information as displayed - NO statistics or complex groupings.
 */

export class PlacesListPDFLayout {
  constructor() {
    this.layoutType = 'table';
    this.name = 'Places List Layout';
  }

  /**
   * Generate PDF content structure for places list
   * @param {Object} pageContext - Page context with places data
   * @returns {Object} PDF content structure
   */
  async generate(pageContext) {
    const placesData = Array.isArray(pageContext.data) ? pageContext.data : [];

    if (placesData.length === 0) {
      return this.generateEmptyContent(pageContext);
    }

    return {
      sections: [
        this.generatePlacesListSection(placesData, pageContext)
      ].filter(section => section !== null)
    };
  }


  /**
   * Generate main places list section - SIMPLIFIED FORMAT
   * Simple list showing only place names and addresses (when available)
   * No headers, no columns, just clean place listings
   * @param {Array} placesData - Places data
   * @param {Object} pageContext - Page context
   * @returns {Object} Places list section
   */
  generatePlacesListSection(placesData, pageContext) {
    // Filter places and format as simple list items
    const placeItems = placesData
      .map(place => this.formatPlaceItem(place))
      .filter(item => item !== null); // Remove null entries

    return {
      type: 'list',
      title: null, // No title - clean format
      items: placeItems
    };
  }


  /**
   * Generate content for empty places list
   * @param {Object} pageContext - Page context
   * @returns {Object} Empty content structure
   */
  generateEmptyContent(pageContext) {
    return {
      sections: [{
        type: 'text',
        title: 'No Places Added',
        content: 'This trip does not have any places added yet. Add some places to your trip to generate a more detailed PDF export.'
      }]
    };
  }




  /**
   * Format individual place item for simple list display
   * @param {Object} place - Place object
   * @returns {string|null} Formatted place item or null if should be hidden
   */
  formatPlaceItem(place) {
    const placeName = place.name || 'Unnamed Place';
    const context = place.context || '';
    const address = this.formatAddress(place);

    // Priority: context > address > name only
    if (context) {
      return `${placeName} - ${context}`;
    } else if (address && address !== 'Address not available') {
      return `${placeName} - ${address}`;
    } else {
      return placeName; // Just show the name if no context or address
    }
  }

  /**
   * Format place address for display
   * @param {Object} place - Place object
   * @returns {string} Formatted address
   */
  formatAddress(place) {
    if (place.address) {
      return place.address;
    }

    const parts = [];
    if (place.street) parts.push(place.street);
    if (place.city) parts.push(place.city);
    if (place.country) parts.push(place.country);

    return parts.length > 0 ? parts.join(', ') : 'Address not available';
  }

  /**
   * Format notes and contact information - combines per spec requirements
   * @param {Object} place - Place object
   * @returns {string} Combined notes and contact info
   */
  formatNotesAndContact(place) {
    const info = [];

    // Add notes/description first
    if (place.notes) info.push(place.notes);
    else if (place.description) info.push(place.description);

    // Add contact information
    if (place.phone) info.push(`Tel: ${place.phone}`);
    if (place.email) info.push(`Email: ${place.email}`);
    if (place.website) info.push(`Web: ${this.formatWebsite(place.website)}`);

    return info.length > 0 ? info.join(' | ') : '-';
  }

  /**
   * Format website URL for display
   * @param {string} website - Website URL
   * @returns {string} Formatted website
   */
  formatWebsite(website) {
    if (!website) return '';

    // Remove protocol for cleaner display
    return website.replace(/^https?:\/\//, '').replace(/^www\./, '');
  }


  /**
   * Get preview information for this layout - simplified per spec
   * @param {*} data - Data to analyze
   * @returns {Object} Preview information
   */
  getPreview(data) {
    const placesCount = Array.isArray(data) ? data.length : 0;

    return {
      estimatedPages: Math.max(1, Math.ceil(placesCount / 10)), // Simple table estimate
      sections: ['Places List'], // Only one simple table
      features: [
        'Place names, addresses, and types',
        'Contact information and notes',
        'Basic place information as displayed'
      ],
      dataCount: placesCount
    };
  }
}